"use client";
import React, { useEffect, useState } from 'react';
import { useTheme } from './ThemeProvider';

/**
 * FIX-48: toggle button for ThemeProvider's dark/light state.
 *
 * Renders as if the theme were "dark" until mounted, even when the
 * persisted preference is "light": ThemeProvider's initial render is always
 * 'dark' on the server (and briefly on the client, before its effect reads
 * localStorage), so rendering the real value immediately would make this
 * button's icon/label flip right after hydration - a visible mismatch in
 * dev, and a one-frame flicker in prod. Gating on `mounted` avoids both.
 */
export function ThemeToggle({ className = '' }: { className?: string }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const effective = mounted ? theme : 'dark';
  const next = effective === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      className={`inline-flex items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.03] p-2 transition-colors hover:border-[#00D4FF]/35 hover:bg-[#00D4FF]/[0.08] ${className}`}
    >
      {effective === 'dark' ? (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="4.5" stroke="#00D4FF" strokeWidth="1.5" />
          <path d="M12 2.5v3M12 18.5v3M3.5 12h3M17.5 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" stroke="#00D4FF" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ) : (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.8 6.8 0 0 0 10.5 10.5z" stroke="#5B7FFF" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

export default ThemeToggle;
